import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, bestSrc, srcSet } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { SiteShell } from "@/components/site/SiteShell";
import { TextPopup } from "@/components/site/Popups";
import { Lightbox } from "@/components/site/Lightbox";

const ratioPct = (ar) => {
  const [w, h] = String(ar || "4:3").split(":").map(Number);
  return w && h ? `${(h / w) * 100}%` : "75%";
};

const PasswordGate = ({ slug, onUnlock }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await axios.post(`${API}/pages/${slug}/unlock`, { password: pw });
      sessionStorage.setItem(`jt_page_${slug}`, r.data.token);
      onUnlock(r.data);
    } catch (ex) {
      setErr("Incorrect password");
    }
  };
  return (
    <div className="jt-gate">
      <form onSubmit={submit} data-testid="page-password-form">
        <h1>This page is private</h1>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoFocus data-testid="page-password-input" />
        {err && <div className="jt-gate-err" data-testid="page-password-error">{err}</div>}
        <button type="submit" data-testid="page-password-submit">View page</button>
      </form>
    </div>
  );
};

export default function GalleryPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(false);
  const [lb, setLb] = useState(-1);

  useEffect(() => {
    let alive = true;
    setState("loading");
    setInfo(false);
    setLb(-1);
    window.scrollTo(0, 0);
    const token = sessionStorage.getItem(`jt_page_${slug}`);
    axios
      .get(`${API}/pages/${slug}`, { headers: token ? { "X-Page-Token": token } : {} })
      .then((r) => {
        if (!alive) return;
        setPage(r.data);
        setState("ok");
      })
      .catch((e) => {
        if (!alive) return;
        setState(e?.response?.status === 401 ? "locked" : "missing");
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  useSeo({
    title: page ? page.seo_title || `${page.title} | Joe Tanis Photography` : "Joe Tanis Photography",
    description: page?.seo_description || page?.description?.slice(0, 160),
    path: `/${slug}`,
    noindex: state !== "ok" || !!page?.hidden,
  });

  if (state === "locked") return <PasswordGate slug={slug} onUnlock={(d) => { setPage(d); setState("ok"); }} />;

  const images = Array.isArray(page?.images) ? page.images : [];
  const cols = page?.columns || 2;
  const style = {
    "--cols": cols,
    "--cols-tablet": Math.min(cols, 2),
    "--cols-mobile": page?.columns_mobile || 2,
    "--ratio": ratioPct(page?.aspect_ratio),
  };

  return (
    <SiteShell variant="gallery" infoOpen={info} onInfo={() => setInfo((v) => !v)}>
      {state === "missing" && <div className="jt-empty" data-testid="page-not-found">Page not found</div>}
      {state === "ok" && page && (
        <>
          <h1 className="sr-only">{page.title} — Joe Tanis Photography</h1>
          {page.description && <p className="sr-only">{page.description}</p>}
          <div className="jt-gallery" style={style} data-testid="gallery-grid">
            {images.map((img, i) => (
              <a
                key={img.media_id + i}
                href={bestSrc(img, 2560)}
                className="jt-gallery__item"
                onClick={(e) => {
                  e.preventDefault();
                  setLb(i);
                }}
                data-testid={`gallery-item-${i}`}
              >
                <div className="jt-gallery__ratio" />
                <img
                  src={bestSrc(img, 1600)}
                  srcSet={srcSet(img)}
                  sizes={`(max-width: 767px) ${Math.round(100 / (page.columns_mobile || 2))}vw, (max-width: 1024px) 50vw, ${Math.round(100 / cols)}vw`}
                  alt={img.alt || page.title}
                  loading={i < cols * 2 ? "eager" : "lazy"}
                  decoding="async"
                />
                <span className="jt-gallery__overlay" />
              </a>
            ))}
          </div>
          {(page.description || page.seo_body) && (
            <TextPopup text={page.description} body={page.seo_body} open={info} onClose={() => setInfo(false)} />
          )}
          {lb >= 0 && <Lightbox images={images} index={lb} onIndex={setLb} onClose={() => setLb(-1)} />}
        </>
      )}
    </SiteShell>
  );
}
